import { VOLUME_PRICING_SHADE } from "@faire/deprecated-retailer-visitor-shared/consts/colors";
import { bpsToPercent } from "@faire/deprecated-retailer-visitor-shared/lib/bpsToDecimalAndPercent";
import { bpsToPercentString } from "@faire/marketplace-lib/utils/bpsUtils";
import { Core } from "@faire/slate/foundation/color";
import { IVolumePricingProgressData } from "@faire/web-api--source/indigofair/data/IVolumePricingProgressData";
import { useStrictLocalization } from "@faire/web/common/localization/localize";
import { Column, Row } from "@faire/web/slate/Layout";
import { Typography } from "@faire/web/slate/Typography";
import { cn } from "@faire/web/tools/cn";
import React from "react";

// eslint-disable-next-line import/no-unassigned-import
import "./progressBarAnimation.tw.css";
import { IProgressDotProps } from "./ProgressDot";

interface ISegmentedProgressBarProps extends IProgressBarProps {
  shouldAnimate?: boolean;
  ProgressDot: React.FC<IProgressDotProps>;
}

export interface IProgressBarProps {
  progress?: IVolumePricingProgressData;
  isSmall?: boolean;
}

export const SegmentedProgressBar: React.FC<ISegmentedProgressBarProps> = ({
  progress,
  shouldAnimate,
  isSmall,
  ProgressDot,
}) => {
  const { strictLocalize } = useStrictLocalization();
  const tiers = progress?.volume_pricing_tiers ?? [];
  const discountBps = progress?.current_year_volume_pricing_tier?.discount_bps;
  const currentTierIndex = tiers.findIndex(
    (tier) => tier.discount_bps === discountBps
  );
  const currentTierPercentage = `${(currentTierIndex + 1) * 25}%`;
  const nextTierPercentage = `${bpsToPercent(progress?.progress_towards_next_tier_bps) * 0.25}%`;

  return (
    <Column className="w-full gap-3">
      <div
        className={cn(
          "bg-fs-border-muted relative w-full rounded-xl",
          isSmall ? "h-2" : "h-3"
        )}
      >
        <div className="absolute h-full w-full overflow-hidden rounded-xl">
          <div
            className={cn(
              "absolute top-0 left-0 h-full",
              shouldAnimate ? "animate-current-tier-bar" : undefined
            )}
            style={{
              width: currentTierPercentage,
              backgroundColor: VOLUME_PRICING_SHADE,
            }}
          />
          <div
            className={cn(
              "absolute top-0 h-full",
              shouldAnimate ? "animate-next-tier-bar" : undefined
            )}
            style={{
              width: nextTierPercentage,
              left: currentTierPercentage,
              backgroundColor: VOLUME_PRICING_SHADE,
            }}
          />
          {tiers.map((tier, index) =>
            index !== tiers.length - 1 ? (
              <div
                key={`progress-bar-separator-${tier.discount_bps || index}`}
                className="bg-fs-surface-primary absolute h-full w-0.5"
                style={{
                  left: `${(index + 1) * 25}%`,
                }}
              />
            ) : null
          ) ?? null}
        </div>
        {discountBps ? (
          <ProgressDot
            currentTierPercentage={currentTierPercentage}
            isSmall={isSmall}
            shouldAnimate={shouldAnimate}
          />
        ) : null}
      </div>
      <Row className="relative h-5 w-full">
        <Typography color={Core.text.subdued}>0%</Typography>
        {tiers.map((tier, index) => {
          const translateX = index === tiers.length - 1 ? "-100%" : "-50%";
          return (
            <Typography
              key={`progress-bar-label-${index}`}
              className="absolute"
              style={{
                left: `${(index + 1) * 25}%`,
                transform: `translateX(${translateX})`,
              }}
              color={Core.text.subdued}
            >
              {bpsToPercentString(tier.discount_bps ?? 0, strictLocalize)}
            </Typography>
          );
        }) ?? null}
      </Row>
    </Column>
  );
};
